import React from 'react';
import { v4 as uuidv4 } from 'uuid';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';
import DeleteIcon from '@mui/icons-material/DeleteOutlineOutlined';
import EditIcon from '@mui/icons-material/EditOutlined';
import AddCircleIcon from '@mui/icons-material/AddCircle';

import Accordion from './Accordion';
import FormDialog from './FormDialog/FormDialog';
import type { Form } from '../types/form';
import { FormOperation, useFormDialog } from './FormDialog/useFormDialog';

interface Item {
  id: string;
}

type Props<T extends Item> = {
  items: T[];
  form: Form<T>;
  expanded?: boolean;
  renderItemEntry: (item: T) => React.ReactNode;
  updateItem: (items: T, newItem: boolean) => void;
  deleteItem: (item: T) => void;
};

function AddItemsFormField<T extends Item>({
  items,
  form,
  expanded,
  renderItemEntry,
  updateItem,
  deleteItem,
}: Props<T>) {
  const onChange = React.useCallback(
    (updatedItem: T) => {
      const newItem = items.find((i) => i.id === updatedItem.id);
      updateItem(updatedItem, newItem == null);
    },
    [updateItem]
  );
  const { openDialog, dialogProps } = useFormDialog({ onChange, form });

  return (
    <Accordion
      expanded={expanded}
      accordionSummary={
        <Stack direction='row' width='100%' justifyContent='space-between'>
          <Typography fontSize='18px'>{form.title}</Typography>
          <IconButton
            sx={{ margin: '0 16px 0 auto', padding: '0' }}
            aria-label='edit'
            onClick={(event) => {
              event.stopPropagation();
              const item = {
                id: uuidv4(),
              } as T;
              openDialog(item, FormOperation.CREATE);
            }}
          >
            <AddCircleIcon fontSize='medium' color='primary' />
          </IconButton>
        </Stack>
      }
      accordionDetails={
        <Stack direction='column'>
          {items.map((item) => (
            <Stack
              key={item.id}
              direction='row'
              spacing={2}
              mt={2}
              p='0px 5px 0px 15px'
              borderLeft='1px solid lightgrey'
              alignItems='center'
            >
              {renderItemEntry(item)}
              <IconButton
                aria-label='edit'
                onClick={() => {
                  openDialog(item, FormOperation.UPDATE);
                }}
              >
                <EditIcon />
              </IconButton>
              <IconButton
                color='error'
                aria-label='delete'
                onClick={() => {
                  deleteItem(item);
                }}
              >
                <DeleteIcon />
              </IconButton>
            </Stack>
          ))}
          {dialogProps && <FormDialog {...dialogProps} />}
        </Stack>
      }
    />
  );
}

export default AddItemsFormField;
